import type { ClinicalSummaryParameter } from '../../../services/clinicalSummary';

/**
 * SHER-1057 — the patient's allergy state, derived from the server's precomputed
 * Clinical Summary in ONE place so the banner and the allergy form cannot disagree.
 *
 *   - notDocumented — nothing recorded and never reviewed.
 *   - known         — one or more real allergies (they supersede a stale noKnown).
 *   - noKnown       — reviewed, no known allergies (the NAMC sentinel).
 */
export type AllergyStatus = 'notDocumented' | 'known' | 'noKnown';

export interface AllergyState {
    status: AllergyStatus;
    values: string[];
}

export function deriveAllergyState(parameter: ClinicalSummaryParameter | null | undefined): AllergyState {
    const values = parameter?.values ?? [];
    if (values.length > 0) return { status: 'known', values };
    if (parameter?.noKnown) return { status: 'noKnown', values: [] };
    return { status: 'notDocumented', values: [] };
}

/**
 * "Documented allergies" and "no known allergies" are mutually exclusive, so the
 * no-known action is offered only when nothing is documented AND nothing has been
 * entered in the form at hand. The state argument distinguishes two absences:
 *   - undefined — the host does not track the state: pre-SHER-1057 behaviour.
 *   - null      — the host tracks it but is still reading it: not offered yet,
 *                 since the patient may have documented allergies.
 */
export function canOfferNoKnown(state: AllergyState | null | undefined, enteredCount: number): boolean {
    if (enteredCount > 0 || state === null) return false;
    return state === undefined || state.status === 'notDocumented';
}
